package one.healthium.ui.compose.components

import androidx.compose.foundation.gestures.snapping.rememberSnapFlingBehavior
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.offset
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.material3.LocalTextStyle
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.snapshotFlow
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.drawWithContent
import androidx.compose.ui.graphics.BlendMode
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.CompositingStrategy
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.layout.onSizeChanged
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.sp
import kotlinx.coroutines.flow.distinctUntilChanged
import kotlinx.coroutines.flow.map


private val defaultTextStyle = TextStyle(
    fontSize = 24.sp,
    fontWeight = FontWeight.Normal,
)

@Composable
fun AppPicker(
    items: List<String>,
    onPositionChanged: (Int) -> Unit = {},
    onSelectedItemChanged: (String) -> Unit = {},
    modifier: Modifier = Modifier,
    scrollModifier: Modifier = Modifier,
    startIndex: Int = 0,
    visibleItemsCount: Int = 5,
    textModifier: Modifier = Modifier,
    textStyle: TextStyle = defaultTextStyle,
    textAlign: TextAlign = TextAlign.Start,
    onDrawTopDivider: (offset: Dp) -> Unit = {},
    onDrawBottomDivider: (offset: Dp) -> Unit = {},
    trailing: @Composable () -> Unit = {},
    infinite: Boolean = true,
) {
    val visibleItemsMiddle = visibleItemsCount / 2
    val listScrollCount = if (infinite) Int.MAX_VALUE else items.size
    val listScrollMiddle = listScrollCount / 2
    val listStartIndex = if (infinite) {
        listScrollMiddle - listScrollMiddle % items.size - visibleItemsMiddle + startIndex
    } else {
        startIndex
    }

    fun getItem(index: Int) = items[index % items.size]
    val listState = rememberLazyListState(initialFirstVisibleItemIndex = listStartIndex)
    val flingBehavior = rememberSnapFlingBehavior(lazyListState = listState)
    val itemHeightPixels = remember { mutableStateOf(0) }
    val itemHeightDp = pixelsToDp(itemHeightPixels.value)
    val fadingEdgeGradient = remember {
        Brush.verticalGradient(
            0f to Color.Transparent,
            0.5f to Color.Black,
            1f to Color.Transparent
        )
    }
    LaunchedEffect(listState.isScrollInProgress) {
        if (!listState.isScrollInProgress) {
            onPositionChanged((listState.firstVisibleItemIndex + visibleItemsMiddle) % items.size)
        }
    }
    LaunchedEffect(listState) {
        snapshotFlow { listState.firstVisibleItemIndex }
            .map { index -> getItem(index + visibleItemsMiddle) }
            .distinctUntilChanged()
            .collect(onSelectedItemChanged)
    }
    CompositionLocalProvider(LocalTextStyle provides textStyle) {
        Row(modifier = modifier) {
            LazyColumn(
                state = listState,
                flingBehavior = flingBehavior,
                horizontalAlignment = Alignment.CenterHorizontally,
                modifier = scrollModifier
                    .height(itemHeightDp * visibleItemsCount)
                    .fadingEdge(fadingEdgeGradient)
            ) {
                items(listScrollCount) { index ->
                    Text(
                        modifier = Modifier
                            .onSizeChanged { size -> itemHeightPixels.value = size.height }
                            .then(textModifier),
                        text = getItem(index),
                        maxLines = 1,
                        overflow = TextOverflow.Ellipsis,
                        textAlign = textAlign,
                    )
                }
            }

            Box(
                modifier = Modifier
                    .offset(y = itemHeightDp * visibleItemsMiddle)
                    .height(itemHeightDp),
                contentAlignment = Alignment.Center,
            ) {
                trailing()
            }

            onDrawTopDivider(itemHeightDp * visibleItemsMiddle)
            onDrawBottomDivider(itemHeightDp * (visibleItemsMiddle + 1))
        }
    }
}

private fun Modifier.fadingEdge(brush: Brush) = this
    .graphicsLayer(compositingStrategy = CompositingStrategy.Offscreen)
    .drawWithContent {
        drawContent()
        drawRect(brush = brush, blendMode = BlendMode.DstIn)
    }

@Composable
private fun pixelsToDp(pixels: Int) = with(LocalDensity.current) { pixels.toDp() }